import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import type { OnePasswordConnectorHosting } from "@p0security/integrations-one-password-shared/types";
import {
  p0InvokerEmail,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { DevEnv } from "@p0security/shared/types/environment";
import { assertNever } from "@p0security/util/assertNever";
import { escapeRegExp } from "lodash-es";

import { toHclComment } from "./hcl.ts";
import { CONNECTOR_IMAGES, type ConnectorImage } from "./image.ts";

/** What a connector's Terraform needs from the installer's context: the
 * environment, which names P0's AWS role; the tenant's other installs, which
 * hold P0's Google service account; and the signed-in admin, whose email
 * domain the allowed-domain pattern starts as. A `FrontendInstallContext` is
 * one. */
export type ConnectorContext = DevEnv & {
  installed: Record<string, unknown>;
  user: { email: string | null } | undefined;
};

/** The arguments to {@link renderConnectorTerraform}. */
export type ConnectorArgs = {
  /** The connector's install item id */
  connectorId: string;
  context: ConnectorContext;
  hosting: OnePasswordConnectorHosting;
  /** The connector images to deploy. Defaults to the builds P0 pins. */
  images?: Readonly<
    Record<OnePasswordConnectorHosting["type"], ConnectorImage>
  >;
};

/** Stands in for the customer's domain when the installer doesn't know who's
 * signed in. It matches no real user, so a connector deployed with it grants
 * nothing until the pattern is changed. */
export const PLACEHOLDER_DOMAIN = "example.com";

/** What a connector's Terraform is rendered from on either cloud, resolved
 * once for both. */
type Rendering<Type extends OnePasswordConnectorHosting["type"]> = {
  /** The connector's install item id */
  connectorId: string;
  hosting: Extract<OnePasswordConnectorHosting, { type: Type }>;
  image: ConnectorImage;
  /** The locals that every connector has (see `renderSharedLocals`) */
  sharedLocals: string;
  /** The Terraform name of the connector's resources */
  terraformId: string;
};

// How far a connector's locals are indented, inside their `locals` block.
const LOCALS_INDENT = "    ";

/** The Lambda function's settings. These follow Docker Hub's connector,
 * copied rather than shared so that adding 1Password changes no
 * other integration. */
const LAMBDA_SETTINGS = {
  architecture: "x86_64",
  memorySizeMb: 512,
  timeoutSeconds: 60,
} as const;

/** How many days CloudWatch keeps the connector's logs, unless the customer
 * changes it. */
const LOG_RETENTION_DAYS = 30;

/** The Google APIs the connector's project needs: `iam` and `run` to deploy
 * it, and `secretmanager` because Google checks an API against the calling
 * service account's project, wherever the secret is. Listed as a prerequisite
 * rather than enabled, since the project's other resources depend on them
 * too. */
const GCP_REQUIRED_SERVICES = [
  "iam.googleapis.com",
  "run.googleapis.com",
  "secretmanager.googleapis.com",
];

/** The Terraform name of a connector's resources. One connector's names
 * mustn't clash with another's in the same configuration, and a Terraform
 * name takes no hyphen. */
export const toConnectorTfId = (connectorId: string) =>
  tfId(`p0_1password_connector_${connectorId}`);

/** The domain of `email`, or `undefined` without one. */
export const toEmailDomain = (email: string | null | undefined) =>
  email?.split("@")[1] || undefined;

/**
 * The allowed-domain pattern a connector's Terraform starts with: the
 * signed-in admin's own email domain, anchored at both ends.
 *
 * It's a starting point. The customer edits it in their Terraform to cover
 * everyone P0 may share an item with, and P0 reads back the pattern that's
 * deployed.
 */
export const toSuggestedDomainPattern = (email: string | null | undefined) =>
  `^${escapeRegExp(toEmailDomain(email) ?? PLACEHOLDER_DOMAIN)}$`;

/** The comment on the allowed-domain pattern. */
const DOMAIN_PATTERN_COMMENT = [
  "The email domains the connector shares items with: a regular expression",
  'that the connector tests against the part of an email after the "@".',
  'Nothing anchors it for you, so keep the "^" and "$": without them a',
  "pattern for acme.com also matches notacme.com and acme.com.evil.io. Change",
  "it to cover everyone P0 may share an item with, such as",
  '"^(acme\\\\.com|acme\\\\.io)$" for two domains. The connector won\'t start',
  "without a pattern. P0 can't change it, and shows the one deployed on each",
  '1Password account as "Allowed domains".',
];

/** What every connector's Terraform says about the accounts it serves. */
const SERVES_MANY = toHclComment([
  "One connector serves every 1Password account that names it in P0. Each",
  "account's own Terraform, from P0's installer, lets this connector read that",
  "account's service account token. This configuration grants it no secret.",
]);

/** The locals that every connector's Terraform has, whichever cloud it's on:
 * the name and region P0 invokes the connector by, and the allowed-domain
 * pattern, which starts as the domain of `email`. */
const renderSharedLocals = (
  hosting: OnePasswordConnectorHosting,
  email: string | null | undefined
) =>
  [
    `connector_name   = "${hosting.connectorName.trim()}"`,
    `connector_region = "${hosting.connectorRegion.trim()}"`,
    toHclComment(DOMAIN_PATTERN_COMMENT, LOCALS_INDENT),
    `domain_allow_pattern = ${JSON.stringify(toSuggestedDomainPattern(email))}`,
  ].join(`\n${LOCALS_INDENT}`);

/**
 * Renders the Terraform that deploys a connector on AWS Lambda.
 *
 * It copies the image into ECR and deploys a Lambda function outside any VPC,
 * since 1Password is reached over the internet. The function's role is named
 * after the connector, which is how each account's Terraform finds it, and P0's
 * role can invoke the function.
 *
 * Every Terraform name carries the connector's id, so several connectors can
 * share a configuration once their `required_providers` blocks are merged.
 */
const renderAwsTerraform = (
  {
    connectorId,
    hosting,
    image,
    sharedLocals,
    terraformId: id,
  }: Rendering<"aws">,
  p0RoleName: string
): string => {
  const accountId = hosting.accountId.trim();

  return `# P0's 1Password connector ${connectorId}, on AWS Lambda.
#
# Applying this copies P0's connector image from Docker Hub into an ECR
# repository, which Lambda requires. Run terraform where Docker and the AWS CLI
# are installed and the AWS CLI is signed in to account ${accountId}.
#
${SERVES_MANY}

terraform {
  # terraform_data needs Terraform 1.4.
  required_version = ">= 1.4"
}

terraform {
  # If your configuration already has a required_providers block, add docker to
  # it and delete this block.
  required_providers {
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}

locals {
  ${id} = {
    # Where P0 invokes the connector. P0's installer has the same values.
    account_id       = "${accountId}"
    ${sharedLocals}
    # How many days CloudWatch keeps the connector's logs.
    log_retention_days = ${LOG_RETENTION_DAYS}
    # The role P0 assumes in this account, which invokes the connector.
    p0_role_name = "${p0RoleName}"
    # P0's connector image: its tag on Docker Hub, and the digest that tag must
    # resolve to.
    image_tag    = "${image.tag}"
    image_digest = "${image.digest}"
  }
}

# P0 derives the connector's ARN from the account, region and name above, so the
# connector must be deployed exactly there. Add the credential settings your
# other AWS providers use, such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.connector_region
  allowed_account_ids = [local.${id}.account_id]
}

data "docker_registry_image" "${id}" {
  name = "${image.name}:\${local.${id}.image_tag}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == local.${id}.image_digest
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Copy the current Terraform from P0's 1Password installer, or contact support@p0.dev."
    }
  }
}

resource "aws_ecr_repository" "${id}" {
  provider = aws.${id}

  name = local.${id}.connector_name
  # Tags can't be overwritten, so no one who can push to this repository can
  # replace the image that runs with access to the tokens.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true

  image_scanning_configuration {
    scan_on_push = true
  }
}

# Copies the pinned image from Docker Hub into the ECR repository, tagged with
# the image's tag and this copy's id. ECR's tags can't be overwritten, so each
# copy needs a tag of its own: copying the image again, to roll back or after
# an interrupted apply, pushes a new one.
resource "terraform_data" "${id}_image" {
  triggers_replace = {
    digest         = data.docker_registry_image.${id}.sha256_digest
    repository_url = aws_ecr_repository.${id}.repository_url
    tag            = local.${id}.image_tag
  }

  provisioner "local-exec" {
    # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
    # A failed sign-out doesn't fail the copy once the image is pushed.
    command = <<-EOT
      set -e
      trap 'docker logout "$REGISTRY" || true' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = local.${id}.connector_region
      REGISTRY = split("/", aws_ecr_repository.${id}.repository_url)[0]
      SOURCE   = "${image.name}@\${local.${id}.image_digest}"
      TARGET   = "\${aws_ecr_repository.${id}.repository_url}:\${local.${id}.image_tag}-\${self.id}"
    }
  }
}

# Pulling one platform and pushing it gives ECR that image's own manifest, not
# the tag's multi-platform one, so the digest there can differ from the one
# pinned above. Lambda needs ECR's.
data "aws_ecr_image" "${id}" {
  provider = aws.${id}

  repository_name = aws_ecr_repository.${id}.name
  image_tag       = "\${local.${id}.image_tag}-\${terraform_data.${id}_image.id}"
}

# The connector's role, named after the connector: P0 expects exactly this
# name. Each 1Password account's Terraform adds a policy to it that lets the
# connector read that account's service account token.
resource "aws_iam_role" "${id}" {
  provider = aws.${id}

  name = local.${id}.connector_name
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

# The log group Lambda writes the connector's logs to.
resource "aws_cloudwatch_log_group" "${id}" {
  provider = aws.${id}

  name              = "/aws/lambda/\${local.${id}.connector_name}"
  retention_in_days = local.${id}.log_retention_days
}

# The connector writes its own logs, and no others.
resource "aws_iam_role_policy" "${id}_logs" {
  provider = aws.${id}

  name = "WriteConnectorLogs"
  role = aws_iam_role.${id}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.${id}.arn}:*"
    }]
  })
}

# No vpc_config: the connector calls 1Password over the internet. If your
# 1Password account has a firewall, it must allow the function's egress.
resource "aws_lambda_function" "${id}" {
  provider = aws.${id}

  function_name = local.${id}.connector_name
  description   = "P0 1Password connector ${connectorId}"
  role          = aws_iam_role.${id}.arn
  package_type  = "Image"
  image_uri     = "\${aws_ecr_repository.${id}.repository_url}@\${data.aws_ecr_image.${id}.image_digest}"
  architectures = ["${LAMBDA_SETTINGS.architecture}"]
  memory_size   = ${LAMBDA_SETTINGS.memorySizeMb}
  timeout       = ${LAMBDA_SETTINGS.timeoutSeconds}

  environment {
    variables = {
      DOMAIN_ALLOW_PATTERN = local.${id}.domain_allow_pattern
    }
  }

  # The connector's role can't create a log group, so its own must exist before
  # it runs.
  depends_on = [aws_cloudwatch_log_group.${id}]
}

# P0 invokes the connector as its role in this account, and needs nothing else
# on it.
resource "aws_iam_role_policy" "${id}_invoke" {
  provider = aws.${id}

  name = "\${local.${id}.connector_name}-invoke"
  role = local.${id}.p0_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.${id}.arn
    }]
  })
}

# The connector hosting that P0's installer has, and the role that each
# 1Password account's Terraform grants. It's ready once P0 can invoke the
# connector.
output "${id}" {
  value = {
    account_id       = local.${id}.account_id
    connector_name   = aws_lambda_function.${id}.function_name
    connector_region = local.${id}.connector_region
    role_arn         = aws_iam_role.${id}.arn
  }

  depends_on = [aws_iam_role_policy.${id}_invoke]
}
`;
};

/**
 * Renders the Terraform that deploys a connector on Cloud Run.
 *
 * Cloud Run pulls the image from Docker Hub itself, and the service has no
 * VPC, since 1Password is reached over the internet. The service runs as a
 * service account named after the connector, which is how each account's
 * Terraform finds it. P0's service account can invoke the service and read
 * its invocation URL.
 *
 * Every Terraform name carries the connector's id, so several connectors can
 * share a configuration.
 */
const renderGcpTerraform = (
  {
    connectorId,
    hosting,
    image,
    sharedLocals,
    terraformId: id,
  }: Rendering<"gcp">,
  invokerEmail: string
): string => {
  const projectId = hosting.projectId.trim();
  const services = GCP_REQUIRED_SERVICES.map(
    (service) => `#   - ${service}`
  ).join("\n");

  return `# P0's 1Password connector ${connectorId}, on Cloud Run.
#
# Requires these APIs to be enabled in project ${projectId}:
${services}
#
${SERVES_MANY}

locals {
  ${id} = {
    # Where P0 invokes the connector. P0's installer has the same values.
    project_id       = "${projectId}"
    ${sharedLocals}
    # P0's service account, which invokes the connector.
    invoker_email = "${invokerEmail}"
    # P0's connector image on Docker Hub, by its digest.
    image_digest = "${image.digest}"
  }
}

# The connector's service account, named after the connector: P0 expects
# exactly this name. Each 1Password account's Terraform lets it read that
# account's service account token.
resource "google_service_account" "${id}" {
  project      = local.${id}.project_id
  account_id   = local.${id}.connector_name
  display_name = "P0 1Password connector ${connectorId}"
}

resource "google_cloud_run_v2_service" "${id}" {
  project             = local.${id}.project_id
  name                = local.${id}.connector_name
  location            = local.${id}.connector_region
  deletion_protection = false
  # No VPC: P0 calls the connector from outside Google Cloud, and the connector
  # calls 1Password over the internet. Access is gated by IAM, not origin.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.${id}.email

    containers {
      image = "docker.io/${image.name}@\${local.${id}.image_digest}"

      # The connector checks this against the caller's OIDC token, so it is
      # not redundant with the roles/run.invoker grant.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = local.${id}.invoker_email
      }

      env {
        name  = "DOMAIN_ALLOW_PATTERN"
        value = local.${id}.domain_allow_pattern
      }
    }
  }
}

resource "google_cloud_run_v2_service_iam_member" "${id}_invoker" {
  project  = local.${id}.project_id
  location = local.${id}.connector_region
  name     = google_cloud_run_v2_service.${id}.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:\${local.${id}.invoker_email}"
}

# P0 reads the service to get its invocation URL when it installs. The invoker
# role gives run.routes.invoke only, so it cannot do this read.
resource "google_cloud_run_v2_service_iam_member" "${id}_viewer" {
  project  = local.${id}.project_id
  location = local.${id}.connector_region
  name     = google_cloud_run_v2_service.${id}.name
  role     = "roles/run.viewer"
  member   = "serviceAccount:\${local.${id}.invoker_email}"
}

# The connector hosting that P0's installer has, and the service account that
# each 1Password account's Terraform grants. It's ready once P0 can invoke
# the connector and read its URL.
output "${id}" {
  value = {
    project_id            = local.${id}.project_id
    connector_name        = google_cloud_run_v2_service.${id}.name
    connector_region      = local.${id}.connector_region
    service_account_email = google_service_account.${id}.email
  }

  depends_on = [
    google_cloud_run_v2_service_iam_member.${id}_invoker,
    google_cloud_run_v2_service_iam_member.${id}_viewer,
  ]
}
`;
};

/**
 * Renders the Terraform that deploys a connector where its install item says
 * it runs, for the customer to apply in their own cloud.
 *
 * The connector's `DOMAIN_ALLOW_PATTERN` is a local the customer edits. P0
 * stores no copy of it: it reads back the one the connector is deployed with.
 *
 * @returns The Terraform, or `undefined` for a connector on Cloud Run when
 * P0's Google Cloud integration isn't installed: the connector lets in P0's
 * service account there, and without one it would let in nobody.
 */
export const renderConnectorTerraform = ({
  connectorId,
  context,
  hosting,
  images = CONNECTOR_IMAGES,
}: ConnectorArgs): string | undefined => {
  const rendering = {
    connectorId,
    image: images[hosting.type],
    sharedLocals: renderSharedLocals(hosting, context.user?.email),
    terraformId: toConnectorTfId(connectorId),
  };
  switch (hosting.type) {
    case "aws":
      return renderAwsTerraform(
        { ...rendering, hosting },
        awsRoleName(context, "iam-write")
      );
    case "gcp": {
      const invokerEmail = p0InvokerEmail(context.installed);
      return invokerEmail
        ? renderGcpTerraform({ ...rendering, hosting }, invokerEmail)
        : undefined;
    }
    default:
      throw assertNever(hosting);
  }
};
